// Phase 4 — first-party UX event beacon. Batched fire-and-forget
// posts to POST /api/events. Throttled so a chatty page can't burn
// requests; flushed on visibility-change so a user closing the tab
// doesn't lose the last batch. Matches the "your data, yours"
// posture — no third-party scripts, no third-party SDKs.
//
// Usage: track('reading.save', 'home', { param: 'alk', method: 'hanna' });
//
// Instrumented moments (server reads these to power the admin
// Behavior tab + retention cohorts):
//
//   tab.open                   tab switches in AppShell
//   reading.save               quick-add reading saved (success)
//   dose.preview               dose calculator opened
//   dose.confirm               dose event confirmed
//   history.open               param history sheet opened
//   settings.open              tank settings sheet opened
//   equipment.add_open         "+ Add equipment" flow started
//   equipment.add_done         equipment row + parts added
//   email_prefs.preset         account preset chip tapped

const FLUSH_INTERVAL_MS = 5_000;
const MAX_QUEUE = 50;
const STORAGE_KEY = 'aqs.session_id';
const BUILD_ID = (typeof window !== 'undefined' && window.__AQS_BUILD_ID__) || 'dev';

function sessionId() {
  if (typeof window === 'undefined') return null;
  try {
    let id = sessionStorage.getItem(STORAGE_KEY);
    if (!id) {
      id = `s_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
      sessionStorage.setItem(STORAGE_KEY, id);
    }
    return id;
  } catch {
    return null;
  }
}

let queue = [];
let flushTimer = null;

function scheduleFlush() {
  if (flushTimer) return;
  flushTimer = setTimeout(() => {
    flushTimer = null;
    flushEvents();
  }, FLUSH_INTERVAL_MS);
}

async function flushEvents() {
  if (!queue.length) return;
  const batch = queue;
  queue = [];
  try {
    // navigator.sendBeacon for the tab-close path (browsers gate
    // fetch during unload). Falls back to fetch otherwise so dev
    // tooling can see the request in the network tab.
    const body = JSON.stringify({
      session_id: sessionId(),
      build_id: BUILD_ID,
      events: batch,
    });
    if (typeof navigator !== 'undefined' && navigator.sendBeacon) {
      const blob = new Blob([body], { type: 'application/json' });
      navigator.sendBeacon('/api/events', blob);
      return;
    }
    await fetch('/api/events', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body,
      keepalive: true,
    });
  } catch {
    // Drop the batch on failure — a beacon should never throw into
    // the UI path. The next flush picks up wherever we are.
  }
}

export function track(kind, screen = null, meta = null) {
  if (!kind) return;
  queue.push({ kind, screen, meta, ts: new Date().toISOString() });
  if (queue.length >= MAX_QUEUE) {
    flushEvents();
    return;
  }
  scheduleFlush();
}

// Wire visibility + pagehide listeners so the last queued events get
// delivered when the user closes the tab. Called once from main.jsx.
let wired = false;
export function installEventBeacon() {
  if (wired || typeof window === 'undefined') return;
  wired = true;
  const onHide = () => {
    if (queue.length) flushEvents();
  };
  window.addEventListener('pagehide', onHide);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') onHide();
  });
}
